import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Cable as CableIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import TenantUsersSection, {
  draftsToUserInputs,
  mapUsersToDrafts,
  type TenantUserDraft,
  validateUserDrafts,
} from '../../components/sistema/TenantUsersSection';
import TenantStoreSection from '../../components/sistema/TenantStoreSection';
import masterUserAdminService from '../../services/masterUserAdminService';
import tenantAdminService from '../../services/tenantAdminService';
import { MasterUserAdmin, TenantAdmin, TenantDbConnectionTestResult } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';

const CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

interface TenantFormState {
  code: string;
  name: string;
  domain: string;
  dbConnectionString: string;
  isActive: boolean;
  plan: string;
  expiresAtLocal: string;
  parentTenantId: string;
}

const defaultForm: TenantFormState = {
  code: '',
  name: '',
  domain: '',
  dbConnectionString: '',
  isActive: true,
  plan: '',
  expiresAtLocal: '',
  parentTenantId: '',
};

const toDatetimeLocalValue = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDatetimeLocalValue = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-AR');
};

function validateTenantForm(form: TenantFormState, isCreate: boolean): string | null {
  const code = form.code.trim();
  const name = form.name.trim();
  const domain = form.domain.trim();
  const plan = form.plan.trim();
  const connectionString = form.dbConnectionString.trim();

  if (!code) return 'El código es obligatorio';
  if (!name) return 'El nombre es obligatorio';
  if (code.length > 50) return 'El código no puede superar 50 caracteres';
  if (name.length > 200) return 'El nombre no puede superar 200 caracteres';
  if (/\s/.test(code)) return 'El código no puede contener espacios';
  if (!CODE_PATTERN.test(code)) {
    return 'El código solo puede contener letras, números, guiones y guiones bajos';
  }
  if (domain.length > 255) return 'El dominio no puede superar 255 caracteres';
  if (plan.length > 50) return 'El plan no puede superar 50 caracteres';
  if (!isCreate && !connectionString) return 'La cadena de conexión es obligatoria';
  if (connectionString.length > 2000) return 'La cadena de conexión no puede superar 2000 caracteres';
  if (form.expiresAtLocal.trim() && !fromDatetimeLocalValue(form.expiresAtLocal)) {
    return 'La fecha de vencimiento no es válida';
  }
  return null;
}

const TenantForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<TenantFormState>({ ...defaultForm });
  const [createdAt, setCreatedAt] = useState<string | undefined>();
  const [userRows, setUserRows] = useState<TenantUserDraft[]>([]);
  const [masterUsers, setMasterUsers] = useState<MasterUserAdmin[]>([]);
  const [parentCandidates, setParentCandidates] = useState<TenantAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTest, setConnectionTest] = useState<TenantDbConnectionTestResult | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  const isCreate = id === 'nuevo' || !id;
  const tenantId = useMemo(() => {
    if (isCreate || !id) return null;
    const parsed = parseInt(id, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [id, isCreate]);

  useEffect(() => {
    const loadMasterUsers = async () => {
      try {
        const data = await masterUserAdminService.listPaged({
          pageNumber: 1,
          pageSize: 100,
        });
        setMasterUsers(data.items);
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar usuarios maestros'), {
          variant: 'error',
          preventDuplicate: true,
        });
      }
    };
    void loadMasterUsers();
  }, [enqueueSnackbar]);

  useEffect(() => {
    const loadParents = async () => {
      try {
        const data = await tenantAdminService.listPaged({ pageNumber: 1, pageSize: 100 });
        setParentCandidates(
          data.items.filter((tenant) => !tenant.parentTenantId && tenant.id !== tenantId),
        );
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar tenants madre'), {
          variant: 'error',
          preventDuplicate: true,
        });
      }
    };
    void loadParents();
  }, [enqueueSnackbar, tenantId]);

  const loadTenant = useCallback(async () => {
    if (tenantId == null) return;
    try {
      setLoading(true);
      const [tenant, users] = await Promise.all([
        tenantAdminService.getById(tenantId),
        tenantAdminService.getTenantUsers(tenantId),
      ]);
      setFormData({
        code: tenant.code,
        name: tenant.name,
        domain: tenant.domain ?? '',
        dbConnectionString: tenant.dbConnectionString,
        isActive: tenant.isActive,
        plan: tenant.plan ?? '',
        expiresAtLocal: toDatetimeLocalValue(tenant.expiresAt),
        parentTenantId: tenant.parentTenantId ? String(tenant.parentTenantId) : '',
      });
      setCreatedAt(tenant.createdAt);
      setUserRows(users.length ? mapUsersToDrafts(users) : []);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el tenant'), { variant: 'error' });
      navigate('/sistema/tenants');
    } finally {
      setLoading(false);
    }
  }, [tenantId, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!isCreate && tenantId != null) {
      loadTenant();
    }
  }, [isCreate, tenantId, loadTenant]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'dbConnectionString') setConnectionTest(null);
  };

  const handleTestConnection = async () => {
    const connectionString = formData.dbConnectionString.trim();
    if (!connectionString) {
      enqueueSnackbar('Ingrese una cadena de conexión para probarla.', { variant: 'warning' });
      return;
    }

    try {
      setTestingConnection(true);
      const result = await tenantAdminService.testConnection(connectionString);
      setConnectionTest(result);
      enqueueSnackbar(result.message, { variant: result.ok ? 'success' : 'error' });
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'No se pudo probar la conexión');
      setConnectionTest({
        ok: false,
        category: 'other',
        message,
      });
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setTestingConnection(false);
    }
  };

  const buildPayload = () => ({
    code: formData.code.trim(),
    name: formData.name.trim(),
    domain: formData.domain.trim() || undefined,
    dbConnectionString: formData.dbConnectionString.trim() || undefined,
    isActive: formData.isActive,
    plan: formData.plan.trim() || undefined,
    expiresAt: fromDatetimeLocalValue(formData.expiresAtLocal),
    parentTenantId: formData.parentTenantId ? Number(formData.parentTenantId) : null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateTenantForm(formData, isCreate);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'warning' });
      return;
    }

    if (!isCreate) {
      const userValidationError = validateUserDrafts(userRows);
      if (userValidationError) {
        enqueueSnackbar(userValidationError, { variant: 'warning' });
        return;
      }
    }

    const payload = buildPayload();

    try {
      setSaving(true);
      if (isCreate) {
        await tenantAdminService.create(payload);
        enqueueSnackbar('Tenant creado correctamente', { variant: 'success' });
      } else if (tenantId != null) {
        await tenantAdminService.update(tenantId, {
          ...payload,
          isActive: formData.isActive,
        });
        await tenantAdminService.setTenantUsers(tenantId, draftsToUserInputs(userRows));
        enqueueSnackbar('Tenant actualizado correctamente', { variant: 'success' });
      }
      navigate('/sistema/tenants');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar el tenant'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!isCreate && tenantId == null) {
    return <Navigate to="/sistema/tenants" replace />;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/tenants')}
              sx={{ textTransform: 'none' }}
            >
              Administración de Tenants
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isCreate ? 'Nuevo tenant' : 'Editar tenant'}
            </Typography>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Tabs
                value={currentTab}
                onChange={(_, value) => setCurrentTab(value)}
                sx={{ mb: 3 }}
              >
                <Tab label="General" />
                <Tab label="Usuarios" disabled={isCreate} />
                <Tab label="Tienda" disabled={isCreate} />
              </Tabs>

              <Stack spacing={3}>
                {currentTab === 0 && (
                  <Grid container spacing={3} maxWidth={900}>
                    {!isCreate && tenantId != null && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="ID"
                          value={tenantId}
                          fullWidth
                          disabled
                        />
                      </Grid>
                    )}
                    {!isCreate && (
                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Fecha de alta"
                          value={formatDateTime(createdAt)}
                          fullWidth
                          disabled
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="code"
                        label="Código"
                        value={formData.code}
                        onChange={handleChange}
                        required
                        fullWidth
                        disabled={saving}
                        helperText="Identificador único alfanumérico (sin espacios)"
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="name"
                        label="Nombre"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        fullWidth
                        disabled={saving}
                        helperText="Razón social o nombre comercial"
                        inputProps={{ maxLength: 200 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="domain"
                        label="Dominio"
                        value={formData.domain}
                        onChange={handleChange}
                        fullWidth
                        disabled={saving}
                        helperText="Dominio público del tenant (opcional, único)"
                        inputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="plan"
                        label="Plan"
                        value={formData.plan}
                        onChange={handleChange}
                        fullWidth
                        disabled={saving}
                        helperText="Plan comercial o tipo de licencia (opcional)"
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="expiresAtLocal"
                        label="Vencimiento"
                        type="datetime-local"
                        value={formData.expiresAtLocal}
                        onChange={handleChange}
                        fullWidth
                        disabled={saving}
                        helperText="Fecha de vencimiento del tenant (opcional)"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth disabled={saving}>
                        <InputLabel id="parent-tenant-label">Tenant madre</InputLabel>
                        <Select
                          labelId="parent-tenant-label"
                          label="Tenant madre"
                          value={formData.parentTenantId}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, parentTenantId: String(e.target.value) }))
                          }
                        >
                          <MenuItem value="">Ninguno (BD principal)</MenuItem>
                          {parentCandidates.map((tenant) => (
                            <MenuItem key={tenant.id} value={String(tenant.id)}>
                              {tenant.code} — {tenant.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Si esta BD es secundaria, los maestros se leen por vistas desde la madre.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isActive}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                            }
                            disabled={saving}
                          />
                        }
                        label="Tenant activo"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack spacing={1.5}>
                        <TextField
                          name="dbConnectionString"
                          label="Cadena de conexión"
                          value={formData.dbConnectionString}
                          onChange={handleChange}
                          fullWidth
                          disabled={saving || testingConnection}
                          required={!isCreate}
                          multiline
                          minRows={3}
                          helperText={
                            isCreate
                              ? 'Opcional: si se deja vacía, se genera automáticamente según la configuración del servidor'
                              : 'Cadena de conexión a la base de datos del tenant'
                          }
                          inputProps={{ maxLength: 2000 }}
                        />
                        <Box>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => void handleTestConnection()}
                            disabled={saving || testingConnection || !formData.dbConnectionString.trim()}
                            startIcon={
                              testingConnection ? <CircularProgress size={16} /> : <CableIcon />
                            }
                            sx={{ px: 2.5, py: 1, whiteSpace: 'nowrap' }}
                          >
                            {testingConnection ? 'Probando...' : 'Probar conexión'}
                          </Button>
                        </Box>
                      </Stack>
                      {connectionTest && (
                        <Alert
                          severity={connectionTest.ok ? 'success' : 'error'}
                          sx={{ mt: 1 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {connectionTest.message}
                          </Typography>
                          {(connectionTest.server || connectionTest.database) && (
                            <Typography variant="body2" color="text.secondary">
                              {[
                                connectionTest.server ? `Servidor: ${connectionTest.server}` : null,
                                connectionTest.database ? `Base: ${connectionTest.database}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          )}
                          {connectionTest.detail && !connectionTest.ok && (
                            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                              Detalle: {connectionTest.detail}
                              {connectionTest.sqlErrorNumber != null
                                ? ` (SQL ${connectionTest.sqlErrorNumber})`
                                : ''}
                            </Typography>
                          )}
                        </Alert>
                      )}
                    </Grid>
                  </Grid>
                )}

                {currentTab === 1 && tenantId != null && (
                  <TenantUsersSection
                    users={masterUsers}
                    rows={userRows}
                    disabled={saving}
                    onChange={setUserRows}
                  />
                )}

                {currentTab === 2 && tenantId != null && (
                  <TenantStoreSection tenantId={tenantId} disabled={saving} />
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={saving}
                    onClick={() => navigate('/sistema/tenants')}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="contained" color="primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TenantForm;
